/**
 * useDuplicateCheck — debounced preflight of the tiered duplicate check.
 *
 * Runs while the user types so the form can warn before they hit Save. Deliberately
 * NOT a React Query hook: the payload is the live form state, so every keystroke
 * would mint a new cache key and the cache would only ever accumulate garbage.
 *
 * Two behaviours worth knowing:
 *
 * - **Stale responses are dropped.** Each run takes a sequence number and a late
 *   reply from an older payload is discarded, so a slow lookup for a half-typed
 *   email cannot overwrite the verdict for the finished one.
 * - **A failed probe reports no duplicate.** This is advisory: the create/update
 *   route runs the same check server-side and is what actually enforces it, so a
 *   flaky preflight must not block or falsely accuse. The error is swallowed
 *   rather than toasted for the same reason — there is nothing the user can do
 *   about it, and the save still behaves correctly.
 */
import { useEffect, useRef, useState } from 'react';
import { duplicateCheckApi } from '../services/contactsApi';
import type { DuplicateCheckPayload, DuplicateMatch } from '../types';

const DEBOUNCE_MS = 400;

/** Identity fields worth probing on — anything else can't produce a match. */
const PROBE_FIELDS = [
  'name',
  'domain',
  'website',
  'email',
  'phone',
  'mobile',
  'first_name',
  'last_name',
] as const;

export function useDuplicateCheck(payload: DuplicateCheckPayload | null): {
  match: DuplicateMatch | null;
  isChecking: boolean;
} {
  const [match, setMatch] = useState<DuplicateMatch | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const seqRef = useRef(0);

  // Serialize the payload so the effect re-runs on VALUE change, not identity —
  // callers build the object inline from form state, so it is new every render.
  const key = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    if (!key) {
      setMatch(null);
      return;
    }
    const parsed: DuplicateCheckPayload = JSON.parse(key);
    const hasSomethingToProbe = PROBE_FIELDS.some((f) => {
      const value = parsed[f];
      return typeof value === 'string' && value.trim().length > 0;
    });
    if (!hasSomethingToProbe) {
      setMatch(null);
      return;
    }

    const seq = ++seqRef.current;
    setIsChecking(true);
    const timer = setTimeout(() => {
      duplicateCheckApi
        .check(parsed)
        .then((result) => {
          if (seq !== seqRef.current) return;
          setMatch(result);
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setMatch(null);
        })
        .finally(() => {
          if (seq !== seqRef.current) return;
          setIsChecking(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [key]);

  return { match, isChecking };
}
